/**
 * useSearch — calls search.fast RPC for combined search with optional mentions.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useAppRuntime } from "../../providers/AppRuntimeProvider";

export interface SearchResultItem {
  readonly id: string;
  readonly name: string | null;
  readonly schema_id: string;
  readonly score: number;
  readonly link_kind?: string;
  readonly data: Readonly<Record<string, unknown>> | null;
}

interface UseSearchResult {
  readonly results: readonly SearchResultItem[];
  readonly isSearching: boolean;
}

export function useSearch(
  query: string,
  mentionIds: readonly string[],
  active: boolean,
): UseSearchResult {
  const runtime = useAppRuntime();
  const [results, setResults] = useState<readonly SearchResultItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const genRef = useRef(0);

  const doSearch = useCallback(
    async (q: string, mentions: readonly string[], gen: number) => {
      if (!q.trim()) {
        if (genRef.current === gen) {
          setResults([]);
          setIsSearching(false);
        }
        return;
      }

      try {
        setIsSearching(true);
        const params: Record<string, unknown> = {
          query: q,
          limit: 20,
        };
        if (mentions.length > 0) {
          params.mention_ids = mentions;
        }

        const resp = await runtime.transport.rpc<{
          results: readonly SearchResultItem[];
        }>("search.fast", params);

        if (genRef.current === gen) {
          setResults(resp.results);
        }
      } catch {
        if (genRef.current === gen) {
          setResults([]);
        }
      } finally {
        if (genRef.current === gen) {
          setIsSearching(false);
        }
      }
    },
    [runtime.transport],
  );

  useEffect(() => {
    if (!active) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    clearTimeout(timerRef.current);
    const gen = ++genRef.current;

    timerRef.current = setTimeout(() => {
      void doSearch(query, mentionIds, gen);
    }, 150);

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => { clearTimeout(timerRef.current); };
  }, [query, mentionIds, active, doSearch]);

  return { results, isSearching };
}
